import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Truck, RefreshCw } from 'lucide-react';
import { listTrasportatori } from '../api/trasportatori';
import type { Trasportatore, TrasportatoreList } from '../types/trasportatore';
import { useAuthStore } from '../store/authStore';
import { SkeletonRow } from '../components/ui/Skeleton';
import { ANAGRAFICHE_TECNICHE_WRITE_ROLES } from '../lib/constants';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function Pagination({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  const nums: (number | '...')[] = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i++) nums.push(i);
  } else {
    nums.push(1, 2);
    if (page > 4) nums.push('...');
    for (let i = Math.max(3, page - 1); i <= Math.min(pages - 2, page + 1); i++) nums.push(i);
    if (page < pages - 3) nums.push('...');
    nums.push(pages - 1, pages);
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button type="button" onClick={() => onPage(page - 1)} disabled={page === 1}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">‹</button>
      {nums.map((n, i) => n === '...' ? (
        <span key={`e-${i}`} className="px-2 text-ink-faint text-sm">…</span>
      ) : (
        <button key={n} type="button" onClick={() => onPage(n as number)}
          className={`px-3 py-1.5 text-sm border rounded-md transition-colors ${
            n === page ? 'bg-sage text-white border-sage font-semibold'
                       : 'border-stone-border text-ink-mid hover:bg-stone-bg'}`}>
          {n}
        </button>
      ))}
      <button type="button" onClick={() => onPage(page + 1)} disabled={page === pages}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">›</button>
    </div>
  );
}

const PAGE_LIMIT = 25;

export default function TrasportatoriPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const canCreate = (ANAGRAFICHE_TECNICHE_WRITE_ROLES as string[]).includes(user?.role ?? '');

  const [data, setData] = useState<TrasportatoreList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await listTrasportatori({
          page, limit: PAGE_LIMIT,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        setData(result);
      } catch {
        setError(t('anagrafiche.trasportatori.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [page, debouncedSearch, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const items: Trasportatore[] = data?.items ?? [];

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-dark tracking-tight mb-1">{t('anagrafiche.trasportatori.titolo')}</h1>
          {data && (
            <span className="text-sm text-ink-soft">
              <strong className="text-ink-dark font-semibold">{data.total}</strong> {t('anagrafiche.trasportatori.totali_suffix')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border border-stone-border rounded-lg text-sm text-ink-mid hover:bg-stone-bg transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('anagrafiche.trasportatori.aggiorna')}
          </button>
          {canCreate && (
            <button type="button"
              data-testid="btn-nuovo-trasportatore"
              onClick={() => navigate('/trasportatori/nuovo')}
              className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark transition-colors">
              <Plus size={15} />
              {t('anagrafiche.trasportatori.nuovo')}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-stone-mid rounded-xl px-4 py-3 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('anagrafiche.trasportatori.cerca_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage text-ink-dark" />
        </div>
        {search && (
          <button type="button" onClick={() => setSearch('')}
            className="text-sm text-ink-soft hover:text-ink-dark transition-colors">
            {t('anagrafiche.trasportatori.pulisci')}
          </button>
        )}
      </div>

      {error ? (
        <div className="bg-accent-danger-bg border border-[#FECACA] rounded-xl p-6 text-center">
          <p className="text-accent-danger font-medium mb-3">{error}</p>
          <button type="button" onClick={fetchData} className="text-sm text-sage underline">{t('anagrafiche.trasportatori.riprova')}</button>
        </div>
      ) : !loading && items.length === 0 ? (
        <div className="bg-white border border-stone-mid rounded-xl p-12 text-center">
          <Truck size={40} className="text-ink-faint mx-auto mb-3" />
          <p className="text-ink-mid font-medium">{t('anagrafiche.trasportatori.vuoto')}</p>
          {debouncedSearch && <p className="text-ink-soft text-sm mt-1">{t('anagrafiche.trasportatori.modifica_ricerca')}</p>}
          {canCreate && !debouncedSearch && (
            <button type="button" onClick={() => navigate('/trasportatori/nuovo')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark transition-colors">
              <Plus size={14} />
              {t('anagrafiche.trasportatori.aggiungi_primo')}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="table-trasportatori">
            <thead>
              <tr className="border-b border-stone-mid bg-stone-bg">
                {[t('anagrafiche.campi.ragione_sociale'), t('anagrafiche.trasportatori.col_anga'), t('anagrafiche.campi.data_rilascio'), t('anagrafiche.campi.categorie_classe'), t('anagrafiche.produttori.col_piva'), t('anagrafiche.produttori.col_aggiornato')].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{h}</th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
                : items.map((tr) => (
                  <tr key={tr.id}
                    data-testid="row-trasportatore"
                    className="border-b border-stone-mid last:border-0 hover:bg-stone-bg transition-colors cursor-pointer"
                    onClick={() => navigate(`/trasportatori/${tr.id}`)}>
                    <td className="px-4 py-3 font-medium text-ink-dark max-w-[220px] truncate">{tr.ragione_sociale}</td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-mid">{tr.numero_iscrizione_anga}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{formatDateIT(tr.data_rilascio)}</td>
                    <td className="px-4 py-3 text-ink-mid">{tr.categorie_classe}</td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-mid">{tr.piva ?? <span className="text-ink-faint">—</span>}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{formatDateIT(tr.modificato_at ?? tr.created_at)}</td>
                    <td className="px-4 py-3">
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/trasportatori/${tr.id}`); }}
                        className="px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-md hover:bg-sage-light transition-colors">
                        {t('anagrafiche.trasportatori.apri')}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!loading && data && (
            <div className="px-4 py-3 border-t border-stone-mid bg-stone-bg flex items-center justify-between flex-wrap gap-3">
              <span className="text-xs text-ink-soft">
                {t('anagrafiche.trasportatori.footer', { items: items.length, total: data.total, page: data.page, pages: data.pages })}
              </span>
              <Pagination page={data.page} pages={data.pages} onPage={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
